import { verifyReceiptPackage } from "@sequesign/lib/verify";
import type {
  AgentActionReceipt,
  ActionRecord,
  BatchInclusionProof,
  EvidenceBlob,
  VerificationReport
} from "@sequesign/lib/types";
import type { CompletenessChecker, InclusionProofFetcher, AuthorKey } from "@sequesign/lib/verify";
import type { WitnessKey } from "@sequesign/lib/types";
import { installReceiptInVirtualFs } from "../load/install.js";
import { getFile, listDirectory } from "../shims/virtual-fs.js";
import path from "path-browserify";
import type { LoadedReceipt } from "../load/types.js";

export interface RunVerificationResult {
  report: VerificationReport;
  receipt: AgentActionReceipt | null;
  actions: ActionRecord[];
  evidence: Map<string, EvidenceBlob>;
  sourceLabel: string;
  packageDirectory: string;
}

export interface CompletenessVerificationOptions {
  baseUrl: string;
  apiKey: string;
}

export interface InclusionProofFetchOptions {
  baseUrl: string;
  // The hosted witness's /log/inclusion/{position} endpoint is
  // public (protocol-spec section 16.6); the verifier omits the
  // Authorization header by default. A self-hosted witness whose
  // operator opted into auth on this endpoint can still receive a
  // Bearer token by passing one through here.
  apiKey?: string;
}

export async function runVerification(
  loaded: LoadedReceipt,
  options: {
    // The trust anchor (required): the witness keys fetched from the
    // witness's well-known endpoint. Verification is an external,
    // third-party trust check, so the report is stamped
    // trust_anchor_mode "external".
    trustedWitnessKeys: WitnessKey[];
    // The platform registration keys (best-effort): anchor the agent
    // identity_proof and approver/counterparty identity_proofs that set the
    // identity tier. Not required for a valid verification — empty just means
    // those legs read self_asserted / present_unverified.
    trustedRegistrationKeys?: WitnessKey[];
    // Template system Phase 5: the platform template-author keys (best-effort):
    // grade template_authenticity ("attested" when the embedded profile's author
    // signature verifies against one of these). Empty just means the grade stays
    // unrecognized/unattested.
    trustedAuthorKeys?: AuthorKey[];
    completeness?: CompletenessVerificationOptions;
    inclusionProofs?: InclusionProofFetchOptions;
  }
): Promise<RunVerificationResult> {
  installReceiptInVirtualFs(loaded);

  const envelopePath = path.join(loaded.packageDirectory, "receipt.json");
  const actionsPath = path.join(loaded.packageDirectory, "actions.jsonl");
  const evidenceDir = path.join(loaded.packageDirectory, "evidence");

  const receipt = parseJsonFile<AgentActionReceipt>(envelopePath);
  const actions = parseJsonlFile<ActionRecord>(actionsPath);
  const evidence = new Map<string, EvidenceBlob>();
  const evidenceFiles = listDirectory(evidenceDir) ?? [];
  for (const name of evidenceFiles) {
    const filePath = path.join(evidenceDir, name);
    const blob = parseJsonFile<EvidenceBlob>(filePath);
    if (blob) evidence.set(blob.action_id, blob);
  }

  const completeness = options.completeness
    ? buildBrowserCompletenessChecker(options.completeness)
    : undefined;
  const inclusionProofs = options.inclusionProofs
    ? buildBrowserInclusionProofFetcher(options.inclusionProofs)
    : undefined;
  const report = await verifyReceiptPackage(loaded.packageDirectory, {
    trustedWitnessKeys: options.trustedWitnessKeys,
    trustedRegistrationKeys: options.trustedRegistrationKeys,
    trustedAuthorKeys: options.trustedAuthorKeys,
    trustAnchorMode: "external",
    completeness,
    inclusionProofs
  });

  return {
    report,
    receipt,
    actions,
    evidence,
    sourceLabel: loaded.sourceLabel,
    packageDirectory: loaded.packageDirectory
  };
}

function buildBrowserCompletenessChecker(
  opts: CompletenessVerificationOptions
): CompletenessChecker {
  const baseUrl = opts.baseUrl.replace(/\/+$/, "");
  return {
    async query({ chainId }) {
      const url = `${baseUrl}/log/completeness?chain_id=${encodeURIComponent(chainId)}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { authorization: `Bearer ${opts.apiKey}` }
      });
      if (!response.ok) {
        const body = await response.text().catch(() => "");
        throw new Error(`Witness log completeness query returned HTTP ${response.status}: ${body}`);
      }
      const data = (await response.json()) as {
        log_id: string;
        chain_id: string;
        entry_positions: number[];
        entry_count: number;
      };
      return {
        log_id: data.log_id,
        chain_id: data.chain_id,
        entry_positions: data.entry_positions,
        entry_count: data.entry_count
      };
    }
  };
}

function buildBrowserInclusionProofFetcher(
  opts: InclusionProofFetchOptions
): InclusionProofFetcher {
  const baseUrl = opts.baseUrl.replace(/\/+$/, "");
  return {
    async fetch({ logId, position, entryHash }) {
      // The witness's /log/inclusion/{position} endpoint is public
      // (protocol-spec section 16.6) and requires log_id as a query
      // parameter. The receipt carries it on every
      // witness_attestations[i].log_entry, so the verifier always
      // has it. An API key is no longer required and is omitted by
      // default; if the deployment's operator opted into auth on
      // their private witness, callers can still pass one via opts.
      const url = `${baseUrl}/log/inclusion/${position}?log_id=${encodeURIComponent(logId)}`;
      const headers: Record<string, string> = {};
      if (opts.apiKey) headers.authorization = `Bearer ${opts.apiKey}`;
      const response = await fetch(url, { method: "GET", headers });
      if (response.status === 200) {
        const proof = (await response.json()) as BatchInclusionProof;
        return { status: "found", proof };
      }
      if (response.status === 202) {
        return { status: "pending", logId, position, entryHash };
      }
      if (response.status === 404) {
        return { status: "not_found", logId, position };
      }
      throw new Error(`Witness GET /log/inclusion/${position} returned HTTP ${response.status}`);
    }
  };
}

function parseJsonFile<T>(filePath: string): T | null {
  const text = getFile(filePath);
  if (text === undefined) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

function parseJsonlFile<T>(filePath: string): T[] {
  const text = getFile(filePath);
  if (text === undefined) return [];
  return text
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0)
    .map((line) => JSON.parse(line) as T);
}
